import * as React from 'react'

import * as Zendesk from '@doist/help-center-utils'
import { scrollTo } from '@doist/marketist'

import { Footer, Seo } from 'components'
import { BodyBackgroundWhite } from 'components/body-background-white'
import * as Sections from 'components/help/articles'
import { HelpHeader } from 'components/help/shared/Header/Header'
import { helpCenterAllowedCategories } from 'config/constants'
import compact from 'lodash/compact'
import { GetServerSidePropsContext } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { getCommonServerSideProps } from 'services'
import { circuitBreaker } from 'services/circuit-breaker'
import { captureException } from 'services/error-tracking'
import { getContent } from 'services/help-center/content'
import { useLoggedUser } from 'services/hooks/useLoggedUser'
import { getOgImageURL } from 'services/ogimage'

type ArticlePageProps = {
    categories: Zendesk.PartialCategory[]
    article: Zendesk.Article
    relatedArticles: Zendesk.PartialArticle[]
    category: Zendesk.PartialCategory
    section: Zendesk.PartialSection
}

function scrollToAnchor() {
    const anchor = location.hash.replace(/#/, '')
    scrollTo(anchor, { offset: -96 })
}

export default function ArticlePage({
    categories,
    article,
    relatedArticles,
    category,
    section,
}: ArticlePageProps) {
    const { i18n, t } = useTranslation('helpcenter')
    const utm_campaign = 'helpcenter'
    const user = useLoggedUser()
    const isDownloadsPage = article.id === 115003645005
    const ogImage = isDownloadsPage
        ? [
              {
                  url: getOgImageURL('og-image-downloads.jpg', i18n.language),
                  alt: t('seo.downloadsImageAlt'),
              },
          ]
        : []

    /** Updates the browser route in case we came from a Zendesk original route. */
    React.useEffect(() => {
        /** Scroll to anchor if there is one in the URL */
        if (location.hash.length > 1) {
            /** We need to wait a bit that the article content is correctly loaded. */
            setTimeout(scrollToAnchor, 500)
        }
    }, [])

    return (
        <>
            <Seo
                path={article.href}
                title={article.title}
                description={article.snippet || ''}
                lang={i18n.language}
                images={ogImage}
            />

            <BodyBackgroundWhite />

            <HelpHeader
                user={user}
                articleId={article.id}
                categories={categories}
                currentPath={article.href}
                searchBarEnabled
            />

            <main>
                <Sections.IntroSection article={article} category={category} section={section} />
                <Sections.ArticleContent
                    user={user}
                    article={article}
                    relatedArticles={relatedArticles}
                />
            </main>

            <Footer utm_campaign={utm_campaign} backgroundColor="var(--color-grey-02)" />
        </>
    )
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
    const { query } = ctx
    const { locale, ...commonProps } = await getCommonServerSideProps(ctx)

    const id = Zendesk.resolveArticleIdFromPath(query.slug as string)

    const content = await getContent(locale)
    const article = Zendesk.findArticle(id ?? (query.slug as string), content)

    if (!article) {
        return {
            redirect: {
                destination: '/help/error',
                permanent: false,
            },
        }
    }

    if (ctx.resolvedUrl !== article.href) {
        return {
            redirect: {
                destination: article.href,
                permanent: false,
            },
        }
    }

    const validCategories = content.categories.filter((c) =>
        helpCenterAllowedCategories.includes(c.id),
    )
    const categories = Zendesk.getPartialCategories(validCategories)

    const { category, section } = Zendesk.getCategoryAndSectionByArticleId(article.id, categories)

    const relatedArticleIds: number[] = compact(
        article.labels.map((label: string) => {
            if (label.includes('related-')) {
                const idStr = label.replace(/related-/, '')
                const id = parseInt(idStr, 10)
                if (!isNaN(id)) {
                    return id
                }
            }
            return undefined
        }),
    )
    const relatedArticles = Zendesk.findArticles(
        relatedArticleIds,
        Zendesk.getPartialArticles(content.articles),
    )
    if (relatedArticles.length < 4) {
        try {
            const api = Zendesk.initializeZendeskApi(locale)
            const results = await circuitBreaker(() => api.searchArticles(article.title))
            const missing = 4 - relatedArticles.length
            relatedArticles.push(...results.slice(0, missing))
        } catch (error) {
            console.error(ctx.resolvedUrl, 'searchArticles timeout')
            captureException(error, { extra: { page: ctx.resolvedUrl } })
        }
    }

    return {
        props: {
            categories,
            article,
            category,
            section,
            relatedArticles,
            ...commonProps,
            ...(await serverSideTranslations(locale, ['common', 'helpcenter'])),
        },
    }
}
